// ============================================================================
// MarketRequestCard — карточка заявки в «Моих заявках» (без таблиц).
// Данные — уже подготовленная строка MarketRequest (представления ссылок и
// подпись статуса приходят с сервера/из lib/market).
// ============================================================================

import { ChevronRight, MapPin, PhoneOff, UserRound } from 'lucide-react';
import { formatValue } from '@/lib/format';
import { isMarketPhoneBlocked, marketRequestTitle, type MarketRequest } from '@/lib/market';
import { MarketStatusChip } from './MarketStatusChip';
import { MarketRatingBadge } from './MarketRatingBadge';

interface MarketRequestCardProps {
  request: MarketRequest;
  onClick: () => void;
}

export function MarketRequestCard({ request, onClick }: MarketRequestCardProps) {
  const created = formatValue(request.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const title = request.descr?.trim() || marketRequestTitle(request);
  const phoneBlocked = request.status === 'new' && isMarketPhoneBlocked(request);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-2xl border bg-card p-4 text-left shadow-sm transition-colors active:bg-accent/40 hover:border-primary/40"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-sm font-semibold leading-5">{title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            {request.categoryName && (
              <span className="rounded-full bg-muted px-2 py-0.5">{request.categoryName}</span>
            )}
            {created && <span>{created}</span>}
          </div>
        </div>
        <MarketStatusChip status={request.status} label={request.statusLabel} />
      </div>

      <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
        {request.address && (
          <div className="flex items-start gap-1.5">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-2">{request.address}</span>
          </div>
        )}
        {(request.masterName || request.masterCatalogId) && (
          <div className="flex flex-wrap items-center gap-1.5">
            <UserRound className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {request.masterName ? `Мастер: ${request.masterName}` : 'Мастер'}
            </span>
            <MarketRatingBadge
              rating={request.masterRating}
              count={request.masterReviewsCount}
              compact
            />
          </div>
        )}
        {phoneBlocked && (
          <div className="flex items-center gap-1.5 font-medium text-amber-800">
            <PhoneOff className="h-3.5 w-3.5 shrink-0" />
            <span>Телефон не подтверждён — мастера не смогут взять заявку</span>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-end text-[11px] font-medium text-primary">
        Открыть <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
      </div>
    </button>
  );
}
